import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight, faSpinner, faBuilding, faClock, faImage, faCamera } from "@fortawesome/free-solid-svg-icons";
import { primaryColor, secondaryColor } from "../../../../utils/comon";
import CustomSelect from "../../../common/formfields/CustomSelect";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const AmenitiesForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [amenityTypes, setAmenityTypes] = useState([]);
    const [daysDropdownOpen, setDaysDropdownOpen] = useState(false);
    
    const employeeCode = storageService.getItem("employeeCode");

    const [formData, setFormData] = useState({
        amenityName: '',
        amenityType: '',
        capacity: '',
        availableDays: [],
        startTime: '00:01',
        endTime: '23:59',
        isBookable: 'No',
        status: 'Active',
        description: '',
        image: ''
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                // Fetch Types
                const typesRes = await apiServices({
                    url: '/ResidentialAmenity/GetAllResidentialAmenityTypes',
                    method: 'GET'
                });
                
                if (typesRes.success && typesRes.data?.data2) {
                    setAmenityTypes(typesRes.data.data2.filter(t => t.isActiveId === 1));
                }

                // If edit mode, fetch amenity data
                if (isEditMode) {
                    const res = await apiServices({
                        url: `/ResidentialAmenity/GetResidentialAmenityById/${id}`,
                        method: 'GET'
                    });

                    if (res.success && res.data?.data?.[0]) {
                        const apiData = res.data.data[0];
                        
                        let isBookableVal = 'No';
                        if (apiData.isbookable === 1 || apiData.isbookable === '1' || apiData.isbookable === true || apiData.isbookable === 'true') {
                            isBookableVal = 'Yes';
                        }

                        setFormData({
                            amenityName: apiData.amenityName || '',
                            amenityType: apiData.amenityTypeId || '',
                            capacity: apiData.capacity || '',
                            availableDays: apiData.availableDays ? apiData.availableDays.split(',').map(d => d.trim()) : [],
                            startTime: apiData.startTime || '00:01',
                            endTime: apiData.endTime || '23:59',
                            isBookable: isBookableVal,
                            status: apiData.isActive === 'Active' ? 'Active' : 'Inactive',
                            description: apiData.description || '',
                            image: apiData.Image || ''
                        });
                    } else {
                        toast.error("Failed to load amenity details.");
                        navigate("/zentres/Amenities-Management");
                    }
                }
            } catch (error) {
                console.error("Error loading form data:", error);
                toast.error("Failed to load form data.");
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [id, isEditMode, navigate]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (id, option) => {
        const val = option?.value || "";
        setFormData(prev => ({ ...prev, [id]: val }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setFormData(prev => ({ ...prev, image: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDayToggle = (day) => {
        setFormData(prev => {
            const days = [...prev.availableDays];
            if (days.includes(day)) {
                return { ...prev, availableDays: days.filter(d => d !== day) };
            } else {
                return { ...prev, availableDays: [...days, day] };
            }
        });
    };

    const toggleSelectAllDays = (e) => {
        if (e.target.checked) {
            setFormData(prev => ({ ...prev, availableDays: [...DAYS_OF_WEEK] }));
        } else {
            setFormData(prev => ({ ...prev, availableDays: [] }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.amenityName || !formData.amenityType || !formData.capacity || formData.availableDays.length === 0) {
            toast.error("Please fill all mandatory fields including Available Days.");
            return;
        }

        if (!/^[A-Za-z\s]+$/.test(formData.amenityName)) {
            toast.error("Amenity Name can only contain letters and spaces.");
            return;
        }

        setSubmitting(true);
        
        const amenityTypeName = amenityTypes.find(a => a.amenityTypeId === Number(formData.amenityType))?.amenityTypeName || '';

        const body = {
            amenityId: isEditMode ? Number(id) : 0,
            amenityName: formData.amenityName,
            amenityTypeId: Number(formData.amenityType),
            amenityTypeName: amenityTypeName,
            status: formData.status,
            capacity: Number(formData.capacity),
            image: formData.image,
            isBookable: formData.isBookable === 'Yes',
            timePeriod: '',
            availableDays: formData.availableDays.join(','),
            startTime: formData.startTime,
            endTime: formData.endTime,
            description: formData.description,
            createdBy: employeeCode || 'System',
            createdOn: new Date().toISOString(),
            modifiedBy: employeeCode || 'System',
            modifiedOn: new Date().toISOString(),
            isActiveId: formData.status === 'Active' ? 1 : 0
        };

        try {
            const url = isEditMode 
                ? `/ResidentialAmenity/PutResidentialAmenity/${id}`
                : `/ResidentialAmenity/PostResidentialAmenity`;
            
            const res = await apiServices({
                url,
                method: isEditMode ? 'PUT' : 'POST',
                data: body
            });

            if (res.success && res.data?.IsSuccess) {
                toast.success(`Amenity ${isEditMode ? 'updated' : 'added'} successfully!`);
                navigate("/zentres/Amenities-Management");
            } else {
                toast.error(res.data?.Message || "Failed to save amenity.");
            }
        } catch (error) {
            console.error("Error saving amenity:", error);
            toast.error("An error occurred while saving.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full w-full bg-white rounded-lg">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-500 text-3xl" />
            </div>
        );
    }

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />

            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEditMode ? 'Update' : 'Add'} Amenity Details`}
                subHeading={`Please fill out the form below to ${isEditMode ? 'update the existing' : 'create a new'} residential amenity.`}
            />

            <div className="flex flex-col gap-8 mt-6">
                {/* Basic Details Section */}
                <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={faBuilding} className="text-sm" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 tracking-tight">Amenity Details</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="col-span-1">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 mb-1 block">Amenity Type <span className="text-red-500">*</span></span>}
                                options={amenityTypes.map(t => ({ label: t.amenityTypeName, value: t.amenityTypeId }))}
                                value={amenityTypes.find(t => String(t.amenityTypeId) === String(formData.amenityType))
                                    ? { label: amenityTypes.find(t => String(t.amenityTypeId) === String(formData.amenityType)).amenityTypeName, value: formData.amenityType }
                                    : null}
                                onChange={(opt) => handleSelectChange("amenityType", opt)}
                                placeholder="Select Amenity Type"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="amenityName">Amenity Name <span className="text-red-500">*</span></label>
                            <input className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" type="text" id="amenityName" value={formData.amenityName} onChange={handleInputChange} placeholder="Enter amenity name" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="capacity">Capacity (Persons) <span className="text-red-500">*</span></label>
                            <input className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" type="number" id="capacity" min="1" value={formData.capacity} onChange={handleInputChange} placeholder="Enter capacity" />
                        </div>
                    </div>
                </div>

                {/* Availability Section */}
                <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={faClock} className="text-sm" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 tracking-tight">Timing & Availability</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="relative">
                            <label className="block text-xs font-bold text-gray-700 mb-1">Available Days <span className="text-red-500">*</span></label>
                            <div 
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white cursor-pointer flex justify-between items-center transition-all hover:border-emerald-300"
                                onClick={() => setDaysDropdownOpen(!daysDropdownOpen)}
                            >
                                <span className="truncate">
                                    {formData.availableDays.length === 0 ? 'Select Days' 
                                      : formData.availableDays.length === 7 ? 'All Days' 
                                      : formData.availableDays.join(', ')}
                                </span>
                                <span className="text-gray-400 text-[10px]">▼</span>
                            </div>
                            
                            {daysDropdownOpen && (
                                <div className="absolute z-[999] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-auto">
                                    <div className="p-2 border-b bg-gray-50/50">
                                        <label className="flex items-center gap-2 cursor-pointer p-2 hover:bg-emerald-50 rounded-md transition-colors">
                                            <input 
                                                type="checkbox" 
                                                checked={formData.availableDays.length === 7}
                                                onChange={toggleSelectAllDays}
                                                className="w-4 h-4 text-emerald-600 rounded border-gray-300"
                                            />
                                            <span className="font-bold text-xs text-gray-700">Select All</span>
                                        </label>
                                    </div>
                                    <div className="p-2">
                                        {DAYS_OF_WEEK.map(day => (
                                            <label key={day} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-emerald-50 rounded-md transition-colors">
                                                <input 
                                                    type="checkbox"
                                                    checked={formData.availableDays.includes(day)}
                                                    onChange={() => handleDayToggle(day)}
                                                    className="w-4 h-4 text-emerald-600 rounded border-gray-300"
                                                />
                                                <span className="text-xs font-medium text-gray-700">{day}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="startTime">Start Time <span className="text-red-500">*</span></label>
                            <input className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all" type="time" id="startTime" value={formData.startTime} onChange={handleInputChange} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="endTime">End Time <span className="text-red-500">*</span></label>
                            <input className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all" type="time" id="endTime" value={formData.endTime} onChange={handleInputChange} />
                        </div>
                        <div>
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 mb-1 block">Is Bookable <span className="text-red-500">*</span></span>}
                                options={[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]}
                                value={formData.isBookable ? { label: formData.isBookable, value: formData.isBookable } : null}
                                onChange={(opt) => handleSelectChange("isBookable", opt)}
                            />
                        </div>
                    </div>
                </div>

                {/* Media Section */}
                <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={faImage} className="text-sm" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 tracking-tight">Media & Description</h3>
                    </div>

                    <div className="flex flex-col md:flex-row gap-6">
                        <div className="flex-shrink-0 flex items-center justify-center">
                            <div className="relative group cursor-pointer w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 hover:border-rose-500 transition-colors bg-white flex items-center justify-center overflow-hidden">
                                {formData.image ? (
                                    <img src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="flex flex-col items-center justify-center text-gray-400 group-hover:text-rose-500 transition-colors">
                                        <FontAwesomeIcon icon={faCamera} className="text-2xl mb-2" />
                                        <span className="text-[10px] font-bold text-center px-2 uppercase tracking-wide">Upload Photo</span>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                            </div>
                        </div>
                        
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="col-span-1 md:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="description">Description</label>
                                <textarea 
                                    id="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows="3"
                                    placeholder="Enter a brief description of the amenity..."
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all resize-none"
                                ></textarea>
                            </div>
                            <div className="col-span-1 md:col-span-1">
                                <CustomSelect
                                    label={<span className="text-xs font-bold text-gray-700 mb-1 block">Status <span className="text-red-500">*</span></span>}
                                    options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                                    value={formData.status ? { label: formData.status, value: formData.status } : null}
                                    onChange={(opt) => handleSelectChange("status", opt)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-between mt-8 border-t border-gray-100 pt-6">
                <button
                    type="button"
                    onClick={() => navigate("/zentres/Amenities-Management")}
                    className="px-4 py-2 border border-gray-200 rounded-xl text-xs font-bold flex items-center gap-2 text-gray-600 hover:bg-gray-50 active:scale-95 transition-all shadow-sm"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <div className="flex gap-3">
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-6 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200 disabled:opacity-50"
                        style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                        onMouseEnter={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = primaryColor();
                                e.currentTarget.style.color = '#FFFFFF';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = secondaryColor();
                                e.currentTarget.style.color = primaryColor();
                            }
                        }}
                    >
                        {submitting ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEditMode ? 'Update Amenity' : 'Save Amenity')}
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AmenitiesForm;
